import type { ReactElement, ComponentType } from 'react'
import { lazy, Suspense } from 'react'
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  Navigate,
} from 'react-router-dom'
import { ProtectedRoute } from '@/components/auth'

// Loading fallback component
function PageLoader(): ReactElement {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-primary" />
    </div>
  )
}

// Wrapper for lazy loaded components
function LazyPage({
  component: Component,
}: {
  component: ComponentType
}): ReactElement {
  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  )
}

// Lazy loaded pages - Public
const HomePage = lazy(() => import('@/pages/public/HomePage'))
const JobsPage = lazy(() => import('@/pages/public/JobsPage'))
const JobDetailPage = lazy(() => import('@/pages/public/JobDetailPage'))
const AboutPage = lazy(() => import('@/pages/public/AboutPage'))
const ContactPage = lazy(() => import('@/pages/public/ContactPage'))
const PricingPage = lazy(() => import('@/pages/public/PricingPage'))
const TermsPage = lazy(() => import('@/pages/public/TermsPage'))
const PrivacyPage = lazy(() => import('@/pages/public/PrivacyPage'))

// Lazy loaded pages - Auth
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'))
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'))
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'))

// Lazy loaded pages - Invite
const AcceptInvitePage = lazy(() => import('@/pages/invite/AcceptInvitePage'))

// Lazy loaded pages - School Dashboard
const SchoolDashboard = lazy(() => import('@/pages/school/DashboardPage'))
const SchoolPostJob = lazy(() => import('@/pages/school/PostJobPage'))
const SchoolEditJob = lazy(() => import('@/pages/school/EditJobPage'))
const SchoolMyJobs = lazy(() => import('@/pages/school/MyJobsPage'))
const SchoolApplications = lazy(() => import('@/pages/school/ApplicationsPage'))
const SchoolDailyCover = lazy(() => import('@/pages/school/DailyCoverPage'))
const SchoolDailyCoverDetail = lazy(
  () => import('@/pages/school/DailyCoverDetailPage')
)
const SchoolAnalytics = lazy(() => import('@/pages/school/AnalyticsPage'))
const SchoolMessages = lazy(() => import('@/pages/school/MessagesPage'))
const SchoolOrganizations = lazy(
  () => import('@/pages/school/OrganizationsPage')
)
const SchoolSettings = lazy(() => import('@/pages/school/SettingsPage'))

// Lazy loaded pages - Recruiter Dashboard
const RecruiterDashboard = lazy(() => import('@/pages/recruiter/DashboardPage'))
const RecruiterPostJob = lazy(() => import('@/pages/recruiter/PostJobPage'))
const RecruiterEditJob = lazy(() => import('@/pages/recruiter/EditJobPage'))
const RecruiterMyJobs = lazy(() => import('@/pages/recruiter/MyJobsPage'))
const RecruiterApplications = lazy(
  () => import('@/pages/recruiter/ApplicationsPage')
)
const RecruiterCVDatabase = lazy(
  () => import('@/pages/recruiter/CVDatabasePage')
)
const RecruiterDailyCover = lazy(
  () => import('@/pages/recruiter/DailyCoverPage')
)
const RecruiterAnalytics = lazy(() => import('@/pages/recruiter/AnalyticsPage'))
const RecruiterMessages = lazy(() => import('@/pages/recruiter/MessagesPage'))
const RecruiterOrganizations = lazy(
  () => import('@/pages/recruiter/OrganizationsPage')
)
const RecruiterSettings = lazy(() => import('@/pages/recruiter/SettingsPage'))

// Lazy loaded pages - Job Seeker Dashboard
const JobSeekerDashboard = lazy(() => import('@/pages/jobseeker/DashboardPage'))
const JobSeekerProfile = lazy(() => import('@/pages/jobseeker/ProfilePage'))
const JobSeekerApplications = lazy(
  () => import('@/pages/jobseeker/ApplicationsPage')
)
const JobSeekerMessages = lazy(() => import('@/pages/jobseeker/MessagesPage'))
const JobSeekerSavedJobs = lazy(() => import('@/pages/jobseeker/SavedJobsPage'))
const JobSeekerAlerts = lazy(() => import('@/pages/jobseeker/AlertsPage'))
const JobSeekerSettings = lazy(() => import('@/pages/jobseeker/SettingsPage'))

// Lazy loaded pages - Admin Dashboard
const AdminDashboard = lazy(() => import('@/pages/admin/DashboardPage'))
const AdminAnalytics = lazy(() => import('@/pages/admin/AnalyticsPage'))
const AdminApprovals = lazy(() => import('@/pages/admin/ApprovalsPage'))
const AdminUsers = lazy(() => import('@/pages/admin/UsersPage'))
const AdminJobs = lazy(() => import('@/pages/admin/JobsPage'))
const AdminImport = lazy(() => import('@/pages/admin/ImportPage'))
const AdminSubscriptions = lazy(() => import('@/pages/admin/SubscriptionsPage'))
const AdminSettings = lazy(() => import('@/pages/admin/SettingsPage'))

// Layouts
const PublicLayout = lazy(() => import('@/components/layout/PublicLayout'))
const DashboardLayout = lazy(
  () => import('@/components/layout/DashboardLayout')
)

// Error pages
const NotFoundPage = lazy(() => import('@/pages/public/NotFoundPage'))

// Route configuration
const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <Suspense fallback={<PageLoader />}>
        <PublicLayout />
      </Suspense>
    ),
    children: [
      { index: true, element: <LazyPage component={HomePage} /> },
      { path: 'jobs', element: <LazyPage component={JobsPage} /> },
      { path: 'jobs/:id', element: <LazyPage component={JobDetailPage} /> },
      { path: 'about', element: <LazyPage component={AboutPage} /> },
      { path: 'contact', element: <LazyPage component={ContactPage} /> },
      { path: 'pricing', element: <LazyPage component={PricingPage} /> },
      { path: 'terms', element: <LazyPage component={TermsPage} /> },
      { path: 'privacy', element: <LazyPage component={PrivacyPage} /> },
    ],
  },
  {
    path: '/auth',
    element: <Outlet />,
    children: [
      { path: 'login', element: <LazyPage component={LoginPage} /> },
      { path: 'register', element: <LazyPage component={RegisterPage} /> },
      {
        path: 'forgot-password',
        element: <LazyPage component={ForgotPasswordPage} />,
      },
      {
        path: 'reset-password',
        element: <LazyPage component={ResetPasswordPage} />,
      },
    ],
  },
  {
    path: '/invite',
    element: <Outlet />,
    children: [
      { path: 'accept', element: <LazyPage component={AcceptInvitePage} /> },
    ],
  },
  {
    path: '/school',
    element: (
      <ProtectedRoute allowedUserTypes={['school']}>
        <Suspense fallback={<PageLoader />}>
          <DashboardLayout userType="school" />
        </Suspense>
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="dashboard" replace /> },
      { path: 'dashboard', element: <LazyPage component={SchoolDashboard} /> },
      { path: 'post-job', element: <LazyPage component={SchoolPostJob} /> },
      {
        path: 'edit-job/:jobId',
        element: <LazyPage component={SchoolEditJob} />,
      },
      { path: 'my-jobs', element: <LazyPage component={SchoolMyJobs} /> },
      {
        path: 'applications',
        element: <LazyPage component={SchoolApplications} />,
      },
      {
        path: 'analytics',
        element: <LazyPage component={SchoolAnalytics} />,
      },
      {
        path: 'daily-cover',
        element: <LazyPage component={SchoolDailyCover} />,
      },
      {
        path: 'daily-cover/:requestId',
        element: <LazyPage component={SchoolDailyCoverDetail} />,
      },
      {
        path: 'messages',
        element: <LazyPage component={SchoolMessages} />,
      },
      {
        path: 'organisations',
        element: <LazyPage component={SchoolOrganizations} />,
      },
      { path: 'settings', element: <LazyPage component={SchoolSettings} /> },
    ],
  },
  {
    path: '/recruiter',
    element: (
      <ProtectedRoute allowedUserTypes={['recruiter']}>
        <Suspense fallback={<PageLoader />}>
          <DashboardLayout userType="recruiter" />
        </Suspense>
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="dashboard" replace /> },
      {
        path: 'dashboard',
        element: <LazyPage component={RecruiterDashboard} />,
      },
      { path: 'post-job', element: <LazyPage component={RecruiterPostJob} /> },
      {
        path: 'edit-job/:jobId',
        element: <LazyPage component={RecruiterEditJob} />,
      },
      { path: 'my-jobs', element: <LazyPage component={RecruiterMyJobs} /> },
      {
        path: 'applications',
        element: <LazyPage component={RecruiterApplications} />,
      },
      {
        path: 'analytics',
        element: <LazyPage component={RecruiterAnalytics} />,
      },
      {
        path: 'cv-database',
        element: <LazyPage component={RecruiterCVDatabase} />,
      },
      {
        path: 'daily-cover',
        element: <LazyPage component={RecruiterDailyCover} />,
      },
      {
        path: 'messages',
        element: <LazyPage component={RecruiterMessages} />,
      },
      {
        path: 'organisations',
        element: <LazyPage component={RecruiterOrganizations} />,
      },
      { path: 'settings', element: <LazyPage component={RecruiterSettings} /> },
    ],
  },
  {
    path: '/jobseeker',
    element: (
      <ProtectedRoute allowedUserTypes={['job_seeker']}>
        <Suspense fallback={<PageLoader />}>
          <DashboardLayout userType="job_seeker" />
        </Suspense>
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="dashboard" replace /> },
      {
        path: 'dashboard',
        element: <LazyPage component={JobSeekerDashboard} />,
      },
      { path: 'profile', element: <LazyPage component={JobSeekerProfile} /> },
      {
        path: 'applications',
        element: <LazyPage component={JobSeekerApplications} />,
      },
      {
        path: 'messages',
        element: <LazyPage component={JobSeekerMessages} />,
      },
      {
        path: 'saved-jobs',
        element: <LazyPage component={JobSeekerSavedJobs} />,
      },
      { path: 'alerts', element: <LazyPage component={JobSeekerAlerts} /> },
      { path: 'settings', element: <LazyPage component={JobSeekerSettings} /> },
    ],
  },
  {
    path: '/admin',
    element: (
      <ProtectedRoute allowedUserTypes={['admin']}>
        <Suspense fallback={<PageLoader />}>
          <DashboardLayout userType="admin" />
        </Suspense>
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate to="dashboard" replace /> },
      { path: 'dashboard', element: <LazyPage component={AdminDashboard} /> },
      { path: 'analytics', element: <LazyPage component={AdminAnalytics} /> },
      { path: 'approvals', element: <LazyPage component={AdminApprovals} /> },
      { path: 'users', element: <LazyPage component={AdminUsers} /> },
      { path: 'jobs', element: <LazyPage component={AdminJobs} /> },
      { path: 'import', element: <LazyPage component={AdminImport} /> },
      {
        path: 'subscriptions',
        element: <LazyPage component={AdminSubscriptions} />,
      },
      { path: 'settings', element: <LazyPage component={AdminSettings} /> },
    ],
  },
  {
    path: '*',
    element: <LazyPage component={NotFoundPage} />,
  },
])

export function AppRouter(): ReactElement {
  return <RouterProvider router={router} />
}
